import { z } from 'zod';
import { uuidSchema, positiveInt, nonNegativeInt, mediumString, shortString, } from './validation.js';
import { DEFAULT_WEIGHT_MIN_KG, DEFAULT_WEIGHT_MAX_KG } from '../data/default-weights.js';
import { baseGarmentAudienceFilterFields, refineBaseGarmentAudienceFilter } from './base-garments.js';
import { costCurrencySchema } from './cost-currency.js';
import { SET_SUPPLY_MAX_SKUS, SUPPLY_COUNT_MAX, stockModeSchema } from './supplier-catalog.js';
const productionTypeSchema = z.enum(['PAP', 'POD']);
/**
 * Print technology for POD products (Phase 50 / BLU-233).
 * Orthogonal to productionType (POD|PAP fulfilment) — applies only when POD.
 * Nullable: meaningful only for POD; PAP / unset = N/A.
 */
const printTechnologySchema = z.enum(['DTG', 'DTF']);
/**
 * BLU-534 display filter: extends the canonical production_type ('PAP'|'POD')
 * with a virtual 'TRIM' value. 'TRIM' maps to skus.is_trim = true and is
 * never written back to the DB — trims keep production_type='PAP' underneath
 * (see inventory.dao.ts listProductsGrouped / listUngroupedSkus / getProducts).
 * Used by the grouped product/SKU listing (Products page) and, as of the
 * Inventory (stock) page type filter, by `getProductsSchema` below. Every
 * other productionType field in this file stays the canonical 2-value
 * `productionTypeSchema`.
 */
const productListFilterSchema = z.enum(['PAP', 'POD', 'TRIM']);
/**
 * Inventory Schemas
 *
 * Shared validation schemas for inventory operations.
 * Used by both backend (tRPC input validation) and frontend (form validation).
 */
/**
 * Get products for a location (or all locations when omitted)
 *
 * `productionType` (Inventory page type filter): optional server-side
 * narrowing to POD / PAP / Trims. See `productListFilterSchema` for the
 * TRIM semantics.
 */
export const getProductsSchema = z
    .object({
    locationId: uuidSchema.optional(),
    productionType: productListFilterSchema.optional(),
    /** "Who can use it" (BLU-1394), derived from the Base garments code of each blank. */
    ...baseGarmentAudienceFilterFields,
})
    .superRefine(refineBaseGarmentAudienceFilter);
/**
 * Get per-location stock breakdown for a single SKU
 */
export const getProductLocationBreakdownSchema = z.object({
    skuId: uuidSchema,
});
/**
 * Get active location assignments for a SKU (sparse inventory rows)
 */
export const getSkuActiveLocationsSchema = z.object({
    skuId: uuidSchema,
});
/**
 * Get the incoming-quantity breakdown for a SKU (BLU-579): the purchase/
 * transfer orders contributing to `incoming_quantity`, for the hover
 * tooltip on the inventory table. `locationId` narrows to a single
 * location; omitted returns the breakdown across all locations.
 */
export const getIncomingBreakdownSchema = z.object({
    skuId: uuidSchema,
    locationId: uuidSchema.optional(),
});
/**
 * Operator-entered reference / order number for a stock adjustment
 * (Thread Lab stock in/out log). Metadata, never identity: single-SKU
 * adjustments write it to `inventory_transactions.reference_id` with
 * `reference_type='adjustment_ref'`; multi-SKU documents store it on the
 * `bulk_import_batches.reference` header instead (rows keep
 * `reference_id=batchId` for undo). Trimmed; empty string is treated as
 * absent by the service layer (Zod `.optional()` still admits '').
 */
export const adjustmentReferenceSchema = z.string().trim().max(64);
/**
 * Add stock to inventory (incremental)
 *
 * BLU-3xx Phase 47.01.2-B-01: optional `targetOrganizationId` lets a 3PL
 * operator route the receive into a connected client's `sku_inventory` row
 * (cross-org). Validated handler-side via `isOrgAccessibleForRow`. Omitted
 * = caller's org (single-tenant byte-equivalent).
 */
export const addStockSchema = z.object({
    skuId: uuidSchema,
    locationId: uuidSchema,
    quantity: positiveInt,
    reason: mediumString.optional(),
    reference: adjustmentReferenceSchema.optional(),
    targetOrganizationId: uuidSchema.optional(),
});
/**
 * Set stock to exact quantity (override)
 *
 * BLU-3xx Phase 47.01.2-B-01: optional `targetOrganizationId` (same semantics
 * as `addStockSchema`).
 */
export const setStockSchema = z.object({
    skuId: uuidSchema,
    locationId: uuidSchema,
    quantity: nonNegativeInt,
    reason: mediumString.optional(),
    reference: adjustmentReferenceSchema.optional(),
    targetOrganizationId: uuidSchema.optional(),
});
/**
 * Deduct stock from inventory
 */
export const deductStockSchema = z.object({
    skuId: uuidSchema,
    locationId: uuidSchema,
    quantity: positiveInt,
    reason: mediumString.optional(),
    source: shortString.optional(),
    referenceType: shortString.optional(),
    referenceId: shortString.optional(),
    /** Operator reference; used only when referenceType/referenceId absent. */
    reference: adjustmentReferenceSchema.optional(),
});
/**
 * BLU-534 gap-fill: structured trim/material scrap recording. Stock
 * reduction flows through the same deduct path as `deductStockSchema`
 * (source='manual', reference_type='scrap'); `sku_scrap_events` holds the
 * reason/PO/notes audit trail on top.
 */
export const SCRAP_REASON_VALUES = [
    'supplier_scrap',
    'damaged_in_transit',
    'qc_reject',
    'other',
    'lost_in_transit',
];
export const scrapReasonSchema = z.enum(SCRAP_REASON_VALUES);
/**
 * The reasons a manual scrap may carry. `lost_in_transit` is reserved for the
 * audit-only rows `resolve_transfer_unreceived` writes, which move no stock; a
 * manual scrap decrements stock, so accepting it here would mix the two
 * meanings. Reads and display keep the full `scrapReasonSchema` list.
 */
export const manualScrapReasonSchema = scrapReasonSchema.exclude(['lost_in_transit']);
export const recordScrapSchema = z.object({
    skuId: uuidSchema,
    locationId: uuidSchema,
    // Capped so an absurd entry can't decrement stock and then overflow the
    // int4 audit column (the audit insert would fail after the decrement applied).
    quantity: positiveInt.max(1_000_000),
    reason: manualScrapReasonSchema,
    referencePoId: uuidSchema.optional(),
    notes: mediumString.optional(),
    /**
     * Operator reference. Written to `inventory_transactions.reference_id`
     * ONLY — `reference_type` stays 'scrap' (contract asserted by tests;
     * changing it would make scrap indistinguishable from manual adjusts).
     */
    reference: adjustmentReferenceSchema.optional(),
});
export const listScrapEventsSchema = z.object({
    skuId: uuidSchema.optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: nonNegativeInt.optional(),
});
/**
 * Create a new SKU at a location
 */
export const createSkuSchema = z.object({
    locationId: uuidSchema,
    sku: shortString.min(1, 'SKU is required'),
    category: z.object({
        type: shortString.optional(),
        size: shortString.optional(),
        color: shortString.optional(),
    }),
    stockLevel: nonNegativeInt,
    notify: nonNegativeInt.optional(),
    imageUrl: z.string().url().optional().or(z.literal('')),
    productionType: productionTypeSchema.optional(),
    /** Phase 50 / BLU-233: print technology (POD only). NULL/unset = N/A. */
    print_technology: printTechnologySchema.nullable().optional(),
    skuCostOverride: z.number().nonnegative().optional(),
    /** BLU-534: trim/component SKU. Forces production_type 'PAP' server-side. */
    isTrim: z.boolean().optional(),
});
/**
 * Bulk create multiple SKUs
 */
export const bulkCreateSkusSchema = z.object({
    locationId: uuidSchema,
    products: z.array(z.object({
        sku: shortString.min(1, 'SKU is required'),
        category: z.object({
            type: shortString.optional(),
            size: shortString.optional(),
            color: shortString.optional(),
        }),
        stockLevel: nonNegativeInt,
        notify: nonNegativeInt.optional(),
        imageUrl: z.string().url().optional().or(z.literal('')),
        productionType: productionTypeSchema.optional(),
        skuCostOverride: z.number().nonnegative().optional(),
        productTitle: shortString.optional(),
    })).min(1, 'At least one product required'),
});
/**
 * Update product details
 */
export const updateProductSchema = z.object({
    productId: uuidSchema,
    locationId: uuidSchema,
    sku: shortString.min(1, 'SKU is required'),
    category: z.object({
        type: shortString.optional(),
        size: shortString.optional(),
        color: shortString.optional(),
    }),
    stockLevel: nonNegativeInt,
    notify: nonNegativeInt.optional(),
    imageUrl: z.string().url().optional().or(z.literal('')),
    productionType: productionTypeSchema.optional(),
    skuCostOverride: z.number().nonnegative().nullable().optional(),
});
/**
 * List SKUs in the organization (global catalog; not location-scoped).
 *
 * BLU-3xx Phase 47.01.2-B-fix3: optional cross-org scoping. When
 * `targetOrganizationId` + `locationId` are both set, the backend gates
 * the request via the same `gateTargetOrganization` helper used by
 * `addStock` (B-01). On success the SKU search is scoped to the target
 * org's catalog instead of the caller's. Unset preserves single-tenant
 * behaviour for existing callers — the new fields are optional.
 */
export const listSkusSchema = z
    .object({
    targetOrganizationId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
})
    .refine((d) => !d.targetOrganizationId || !!d.locationId, {
    message: 'locationId is required when targetOrganizationId is set',
    path: ['locationId'],
});
/**
 * List the full SKU catalog joined with per-location stock for the PO picker.
 * Unlike getProducts (which uses an INNER JOIN view and hides SKUs that have
 * never been stocked at the location), this returns the full org catalog so
 * brand-new locations can still have POs created against them.
 */
export const listSkusForPurchaseOrderSchema = z.object({
    locationId: uuidSchema,
});
// The cost currencies the exchange-rates cron fetches (a leaf module: base-garments reads it too).
export { costCurrencySchema } from './cost-currency.js';
/**
 * Update SKU metadata (global; not location-scoped)
 *
 * Used by Products page (POD only). Stock changes remain location-scoped.
 */
export const updateSkuSchema = z.object({
    skuId: uuidSchema,
    /** Rename the SKU code (POD only). Uppercased and trimmed server-side. */
    sku: shortString.min(1, 'SKU code is required').optional(),
    title: shortString.optional(),
    category: z.object({
        type: shortString.optional(),
        size: shortString.optional(),
        color: shortString.optional(),
    }).optional(),
    imageUrl: z.string().url().optional().or(z.literal('')),
    productionType: productionTypeSchema.optional(),
    /** Phase 50 / BLU-233: print technology (POD only). NULL clears it. */
    print_technology: printTechnologySchema.nullable().optional(),
    skuCostOverride: z.number().nonnegative().nullable().optional(),
    /**
     * BLU-1359: the Default cost's currency. null = the org currency. Only
     * accepted together with skuCostOverride; absent keeps the stored one.
     */
    skuCostOverrideCurrency: costCurrencySchema.nullable().optional(),
    hsCode: z.string().max(20).nullable().optional(),
    weightValue: z.number().nonnegative().nullable().optional(),
    weightUnit: z.enum(['GRAMS', 'KILOGRAMS', 'OUNCES', 'POUNDS']).nullable().optional(),
    /** BLU-271: per-SKU supplier override. null clears back to the category default. */
    supplierId: uuidSchema.nullable().optional(),
});
/**
 * Update sparse location assignments for a SKU.
 * Any selected location gets/keeps a sku_inventory row; unselected locations remove the row.
 */
export const updateSkuActiveLocationsSchema = z.object({
    skuId: uuidSchema,
    locationIds: z.array(uuidSchema),
});
/**
 * Bulk update SKU images (global; not location-scoped)
 */
export const bulkUpdateSkuImagesSchema = z.object({
    skuIds: z.array(uuidSchema).min(1, 'At least one SKU required'),
    imageUrl: z.string().url(),
});
export const getCostOfGoodsSchema = z.object({});
/** BLU-599: a single ranked supplier + its own cost/currency at the category level. */
export const categorySupplierCostSchema = z.object({
    supplierId: uuidSchema,
    rank: z.number().int().min(1),
    unitCost: z.number().nonnegative('Cost must be 0 or more'),
    currency: costCurrencySchema.default('AUD'),
});
export const upsertCostOfGoodsSchema = z.object({
    costs: z.array(z.object({
        productType: shortString.min(1, 'Product type is required'),
        landedCost: z.number().nonnegative('Cost must be 0 or more').optional(),
        landedCostCurrency: costCurrencySchema.default('AUD'),
        factoryCost: z.number().nonnegative('Cost must be 0 or more').optional(),
        factoryCostCurrency: z.enum(['USD', 'AUD']).default('USD'),
        /** BLU-271: default supplier for every SKU of this product type. Kept for back-compat; BLU-599 derives this from `suppliers` rank 1. */
        defaultSupplierId: uuidSchema.nullable().optional(),
        /** BLU-271: backup supplier for this product type. Kept for back-compat; BLU-599 derives this from `suppliers` rank 2. */
        backupSupplierId: uuidSchema.nullable().optional(),
        /** BLU-599: ranked supplier list, each with its own cost + currency. */
        suppliers: z
            .array(categorySupplierCostSchema)
            .default([])
            // Reject duplicate rank / supplier at the boundary so a malformed payload
            // is a clean 400, not a DB unique-constraint 500 from the RPC.
            .superRefine((rows, ctx) => {
            const seenRanks = new Set();
            const seenSuppliers = new Set();
            rows.forEach((row, i) => {
                if (seenRanks.has(row.rank)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [i, 'rank'],
                        message: 'Duplicate supplier rank',
                    });
                }
                seenRanks.add(row.rank);
                if (seenSuppliers.has(row.supplierId)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [i, 'supplierId'],
                        message: 'Duplicate supplier',
                    });
                }
                seenSuppliers.add(row.supplierId);
            });
        }),
    })).min(1, 'At least one product type required'),
});
export const deleteCostOfGoodsSchema = z.object({
    productTypes: z.array(shortString.min(1)).min(1, 'At least one product type required'),
});
/**
 * Bulk add stock to multiple products
 *
 * BLU-3xx Phase 47.01.2-B-01: per-row + parent `targetOrganizationId`.
 * Per-row takes precedence over parent. Both optional; omitted = caller's
 * org (single-tenant byte-equivalent). Handler dedup-validates the SET of
 * distinct target orgs once (no N+1 gate calls).
 */
export const bulkStockDirectionSchema = z.enum(['add', 'consume']);
export const bulkAddStockSchema = z.object({
    locationId: uuidSchema,
    productIds: z.array(z.object({
        id: uuidSchema,
        sku: shortString,
        // Signed: negative stock is a legal state (discrepancies stay visible),
        // and the dialog echoes the row's REAL level — a nonNegative guard here
        // would reject any sheet containing an already-negative row. The service
        // never reads this field.
        currentStock: z.number().int(),
        /**
         * Per-row quantity (bulk stock editor): overrides the shared `quantity`
         * for this SKU. Omitted = the shared quantity applies (legacy
         * same-amount-for-all behaviour, byte-equivalent).
         */
        quantity: positiveInt.optional(),
        targetOrganizationId: uuidSchema.optional(),
    })).min(1, 'At least one product required'),
    /** Shared fallback quantity for rows without a per-row quantity. */
    quantity: positiveInt,
    /** Operator reference for the adjustment document header. */
    reference: adjustmentReferenceSchema.optional(),
    targetOrganizationId: uuidSchema.optional(),
    /**
     * Whole-document direction: 'add' (default) increments, 'consume'
     * decrements every row. One direction per adjustment document — mirrors
     * separate inbound/outbound documents rather than a mixed sheet.
     * Consume never creates inventory rows: a target org on a consume is
     * rejected per-row by the service (existing row's org always wins), so
     * no schema-level guard is needed here.
     */
    direction: bulkStockDirectionSchema.optional(),
}).superRefine((input, ctx) => {
    // All rows carry a quantity, or none do. A mixed payload would silently
    // add the shared fallback (the per-SKU editor sends a meaningless 1) to
    // the rows that omitted theirs — a stock-accuracy bug with no error.
    const withQty = input.productIds.filter((p) => p.quantity != null).length;
    if (withQty !== 0 && withQty !== input.productIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['productIds'],
            message: 'Either every row must carry its own quantity, or none may',
        });
    }
});
/**
 * Delete products from a location
 */
export const deleteProductsSchema = z.object({
    locationId: uuidSchema,
    productIds: z.array(uuidSchema).min(1, 'At least one product required'),
});
/**
 * Bulk update thresholds for multiple SKUs
 */
export const bulkUpdateThresholdsSchema = z.object({
    locationId: uuidSchema,
    updates: z.array(z.object({
        sku: shortString.min(1, 'SKU is required'),
        notify: nonNegativeInt,
    })).min(1, 'At least one threshold update required'),
});
/**
 * Update notify threshold for a single SKU at a single location
 */
export const updateNotifyThresholdSchema = z.object({
    skuId: uuidSchema,
    locationId: uuidSchema,
    notify: nonNegativeInt,
});
/**
 * Per-(sku, location) print-technology routing override (client-catalog
 * routing). Owner-managed locations only (BLU-681 authority); cross-org via
 * targetOrganizationId gated on an ACTIVE junction at `locationId`.
 * `value: null` clears the override (falls back to the sku-level default).
 */
export const setPrintTechnologyOverrideSchema = z.object({
    locationId: uuidSchema,
    targetOrganizationId: uuidSchema.optional(),
    skuIds: z.array(uuidSchema).min(1).max(200),
    value: z.enum(['DTG', 'DTF']).nullable(),
});
/**
 * BLU-1268 Supply: set the supply mode (and, with Managed elsewhere, the fixed
 * store quantity; with Stocked or Just in time, the keep-at-least level) of up
 * to 200 blanks at one location. OWNER-ONLY, always the owner org's own rows: no
 * targetOrganizationId, so a client at a shared floor cannot reach it.
 *
 * Absent key = untouched; `null` = clear. A fixed quantity only goes with
 * Managed elsewhere, so it is refused beside Stocked or Just in time (null is
 * how the browser clears it).
 */
export const setSupplySchema = z.object({
    locationId: uuidSchema,
    skuIds: z.array(uuidSchema).min(1).max(SET_SUPPLY_MAX_SKUS),
    patch: z
        .object({
        stockMode: stockModeSchema.optional(),
        storeFixedQty: z.number().int().min(0).max(SUPPLY_COUNT_MAX).nullable().optional(),
        keepAtLeast: z.number().int().min(0).max(SUPPLY_COUNT_MAX).nullable().optional(),
    })
        .superRefine((patch, context) => {
        if (patch.stockMode === undefined && patch.storeFixedQty === undefined && patch.keepAtLeast === undefined) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose something to change', path: [] });
        }
        if (patch.stockMode !== undefined &&
            patch.stockMode !== 'managed_elsewhere' &&
            patch.storeFixedQty !== undefined &&
            patch.storeFixedQty !== null) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'A fixed store quantity only goes with Managed elsewhere',
                path: ['storeFixedQty'],
            });
        }
    }),
});
/**
 * BLU-1272: per-(client SKU, location) 3PL shipping weight override, in kg per
 * single item. OWNER-ONLY (the org that runs the floor sets it); cross-org via
 * targetOrganizationId gated on an ACTIVE junction at `locationId`. Stored in
 * its own table and never written to the client's Shopify or `skus.weight_value`
 * (D-01); Shopify product sync never touches it (D-02).
 *
 * `weightKg` is `.nullable()` and NOT `.optional()` on purpose (same reasoning
 * as setPushSafetyBufferSchema): `null` means "clear the override" and must
 * travel as an explicit null. Bounds match the default-weights bounds and the
 * table's CHECK. Bulk ceiling mirrors setPrintTechnologyOverrideSchema (200).
 */
export const setShippingWeightOverrideSchema = z.object({
    locationId: uuidSchema,
    targetOrganizationId: uuidSchema.optional(),
    skuIds: z.array(uuidSchema).min(1).max(200),
    weightKg: z
        .number()
        .finite()
        .min(DEFAULT_WEIGHT_MIN_KG, 'Weight must be at least 0.001 kg')
        .max(DEFAULT_WEIGHT_MAX_KG, 'Weight must be 30 kg or less')
        .nullable(),
});
/**
 * BLU-711 Part B: owner-side Shared SKU nomination (`skus.is_shared`).
 *
 * Only the SKU's OWN org may toggle — a 3PL owner nominates ITS OWN SKUs as
 * shared; there is deliberately no `targetOrganizationId` (unlike
 * setPrintTechnologyOverrideSchema above). Disabling while a live
 * `sku_substitution_mappings` row references the SKU is rejected by the DB
 * reverse guard (SQLSTATE P0711); the service pre-counts and surfaces the
 * live-mapping count in a structured `conflict` error so the UI can explain.
 */
export const setSkuSharedSchema = z.object({
    skuId: uuidSchema,
    isShared: z.boolean(),
});
/**
 * Stock Hold-Back layer 1: per-(sku, location) units withheld from Shopify.
 *
 * `value` is `.nullable()` and NOT `.optional()` on purpose. `null` means
 * "clear the override, fall through to the type rule / org fallback" and must
 * travel over the wire as an explicit null; an OMITTED key would read as "no
 * change". `0` is a distinct, meaningful value — "hold back nothing here" —
 * and must not be conflated with either. The frontend's blank-vs-zero handling
 * depends on this distinction being preserved end to end.
 *
 * Bulk ceiling mirrors setPrintTechnologyOverrideSchema (200).
 */
export const setPushSafetyBufferSchema = z.object({
    locationId: uuidSchema,
    targetOrganizationId: uuidSchema.optional(),
    skuIds: z.array(uuidSchema).min(1).max(200),
    value: z.number().int().min(0).max(100000).nullable(),
});
/**
 * Stock Hold-Back layer 2's production-type axis — mirrors the CHECK on
 * `push_safety_buffers.production_type`. `production_type` is part of the
 * layer-2 KEY (with `locationId`/`productType`), not a filter: a PAP finished
 * good and a POD blank can legitimately share the same `productType` string
 * ("T-Shirt") at the same location while needing independent buffers.
 */
const holdBackProductionTypeSchema = z.enum(['POD', 'PAP']);
/**
 * Stock Hold-Back impact preview input: the PENDING grid cells, where
 * `bufferUnits: null` means the cell was cleared (no row ⇒ falls through to the
 * org fallback).
 */
export const previewHoldBackImpactSchema = z.object({
    cells: z
        .array(z.object({
        locationId: uuidSchema,
        productionType: holdBackProductionTypeSchema,
        productType: z
            .string()
            .trim()
            .min(1)
            .max(255)
            .regex(/^[^\p{Cc}]+$/u, 'Product type contains control characters'),
        bufferUnits: z.number().int().min(0).max(100000).nullable(),
    }))
        .max(1000)
        .default([]),
});
/** Stock Hold-Back layer 2 read — no input; own-org only. */
export const getHoldBackRulesSchema = z.object({});
/**
 * Stock Hold-Back layer 2 write. The grid saves every changed cell in one call.
 * A cell cleared to blank arrives in `deletes` (no row ⇒ fall through to the org
 * fallback); a cell set to 0 arrives in `upserts` with `bufferUnits: 0` (an
 * explicit "hold back nothing"). `.superRefine` rejects a duplicate
 * (locationId, productionType, productType) triple at the boundary so a
 * malformed payload is a clean 400 rather than a DB unique-constraint 500.
 */
const holdBackCellSchema = z.object({
    locationId: uuidSchema,
    productionType: holdBackProductionTypeSchema,
    productType: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .regex(/^[^\p{Cc}]+$/u, 'Product type contains control characters'),
});
export const saveHoldBackRulesSchema = z
    .object({
    upserts: z
        .array(holdBackCellSchema.extend({ bufferUnits: z.number().int().min(0).max(100000) }))
        .max(1000)
        .default([]),
    deletes: z.array(holdBackCellSchema).max(1000).default([]),
})
    .superRefine((val, ctx) => {
    const seen = new Set();
    for (const cell of [...val.upserts, ...val.deletes]) {
        const key = `${cell.locationId}::${cell.productionType}::${cell.productType.trim().toLowerCase()}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate hold-back cell for product type "${cell.productType}" at the same location`,
            });
            return;
        }
        seen.add(key);
    }
    if (val.upserts.length === 0 && val.deletes.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Nothing to save' });
    }
});
/**
 * Bulk variant for the /products FloatingSelectionBar "Mark as shared".
 * Bounded like setPrintTechnologyOverrideSchema's bulk ceiling.
 */
export const bulkSetSkuSharedSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(200),
    isShared: z.boolean(),
    /**
     * Phase 92 (BLU-1267, 92-30, D-23): bulk "Stop offering blanks". When true
     * and un-sharing, blanks client products still use stay shared and the
     * rest are un-shared; the result then carries `skippedInUseCount`. Absent
     * or false keeps today's all-or-nothing conflict.
     */
    skipInUse: z.boolean().optional(),
});
/**
 * BLU-711 Part B: live substitution-mapping counts for the caller's OWN
 * shared SKUs (owner-scoped: only mappings whose owner_organization_id is the
 * caller are counted). Feeds the EditSkuModal confirm-on-disable copy.
 */
export const getSharedSkuMappingCountsSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(200),
});
/**
 * BLU-711 Part B: count of live substitution mappings between the caller (as
 * owner) and one client org — the junction-card "N shared SKUs" figure.
 */
export const getClientSharedSkuMappingCountSchema = z.object({
    clientOrganizationId: uuidSchema,
});
/**
 * BLU-711 Part B Stage 2: client-side browse of ONE owner org's nominated
 * (`is_shared`) catalog — the SharedSkuPicker's option source. Server-gated by
 * `assertClientOrSelf`; the read model returns a trimmed projection only
 * (identity/descriptive fields — never costs, supplier, thresholds, or
 * Shopify ids). Bounded page: the picker is search-driven, never a full dump.
 */
export const listOwnerSharedCatalogSchema = z.object({
    ownerOrganizationId: uuidSchema,
    search: z.string().trim().max(128).optional(),
    limit: z.number().int().min(1).max(50).optional(),
    offset: z.number().int().min(0).max(100_000).optional(),
});
/**
 * Phase 92 (BLU-1265, 92-22): one page of an owner org's shared blanks
 * grouped into garments (the supply composer's picker, and the 3PL's own
 * Replace picker). Same gates as `listOwnerSharedCatalogSchema`; the search,
 * the count and the page all run in one SQL statement
 * (`list_owner_shared_garments`), never grouped in TypeScript.
 */
export const listOwnerSharedGarmentsSchema = z.object({
    ownerOrganizationId: uuidSchema,
    search: z.string().trim().max(128).optional(),
    limit: z.number().int().min(1).max(50).optional(),
    offset: z.number().int().min(0).max(100_000).optional(),
});
/**
 * BLU-711 Part B Stage 2: first-ever application write path for
 * `sku_substitution_mappings` (the table's authenticated grants are REVOKEd —
 * writes go through the service-role router only). Caller is the CLIENT;
 * `owner_organization_id` is never accepted from the caller — the DB trigger
 * derives it from the shared SKU row. Bounded like bulkSetSkuSharedSchema.
 */
export const createSharedSkuMappingsSchema = z.object({
    ownerOrganizationId: uuidSchema,
    mappings: z
        .array(z.object({ clientSkuId: uuidSchema, sharedSkuId: uuidSchema }))
        .min(1)
        .max(200),
});
/**
 * BLU-711 Part B Stage 2: un-map. Identity-gated in the service (caller must
 * be the mapping's client OR owner org — either counterparty may withdraw;
 * deliberately NO junction assert so cleanup still works after a junction
 * dies).
 */
export const deleteSharedSkuMappingSchema = z.object({ mappingId: uuidSchema });
/**
 * Phase 92 (BLU-1267, 92-30): "Replace with" on the 3PL's own shared blanks.
 * One pair = the blank client products print on now and the blank they print
 * on from the next order. At most 200 pairs, the SQL's own bound. The owner is
 * always the caller's org (ctx), never an input field. Preview and apply take
 * the same input; both are mutations so the pair list travels in the POST
 * body (review P4).
 */
const replaceBlankPairSchema = z.object({
    fromSkuId: uuidSchema,
    toSkuId: uuidSchema,
});
export const previewReplaceBlankSchema = z.object({
    replacements: z.array(replaceBlankPairSchema).min(1).max(200),
});
export const replaceBlankSchema = z.object({
    replacements: z.array(replaceBlankPairSchema).min(1).max(200),
});
/**
 * Phase 92 (BLU-1267, 92-30): per blank of the caller's own, how many client
 * products print on it ("Used by {count} client products"), counted in SQL by
 * one `count_supply_blank_usage` call. At most 500 ids (the SQL's bound), sent
 * in the POST body (read-only mutation, review P4 and P6).
 */
export const getSupplyBlankUsageSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(500),
});
/**
 * Phase 92 (BLU-1267, 92-30): the bulk "Stop offering blanks" review. Splits
 * the caller's chosen blanks into "still used by client products" and "can
 * stop being offered", from the same single RPC call as the usage read.
 */
export const previewStopOfferingBlanksSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(500),
});
/**
 * Phase 92 (BLU-1267, 92-30 review M2): the message marker of the Stop
 * offering (un-share) conflict whose only cause is supply mappings still held
 * by client orders already placed (open demand, an unfinished unit, or a
 * Replace inside the intake horizon). tRPC drops error causes, so the CONFLICT
 * message STARTS with `${STOP_OFFERING_WAITING_FOR_ORDERS_MARKER}:`; the
 * frontend branches on that prefix. Every other un-share conflict keeps
 * today's message ("... Remove the client mappings first.").
 */
export const STOP_OFFERING_WAITING_FOR_ORDERS_MARKER = 'SUPPLY_WAITING_FOR_ORDERS';
/**
 * Phase 92 (BLU-1267, 92-30): one of the caller's OWN shared garments'
 * blanks with their size and colour, for the Replace value match. The key is
 * the first code segment (1 to 64 characters, the SQL's bound).
 */
export const getOwnSharedGarmentBlanksSchema = z.object({
    garmentKey: z.string().trim().min(1).max(64),
});
export const stockSuggestionIssueTypeSchema = z.enum([
    'insufficient_stock',
    'low_stock_warning',
]);
export const getStockSuggestionsSchema = z.object({
    locationIds: z.array(uuidSchema).min(1).optional(),
    since: z.string().optional(),
    sku: shortString.optional(),
    limit: z
        .number()
        .int()
        .positive()
        .max(500)
        .optional(),
});
/**
 * Delete SKUs globally (removes the skus row; cascades handle inventory)
 */
export const deleteSkusSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(100),
});
/**
 * BLU-1276: archive ("retire") SKUs. Reversible: the row keeps its history and
 * comes back with unretireSkus. The server re-checks usage inside the same SQL
 * statement that flips the flag, so an in-use SKU is refused per SKU (not the
 * whole request). Capped at exactly one server batch (the backend's
 * SKU_RETIREMENT_BATCH_SIZE, 50; keep the two equal) so a request is ONE RPC
 * call: a 25 s gateway timeout can never leave a request half-applied across
 * batches. Larger selections are split by the caller.
 */
export const SKU_RETIREMENT_MAX_IDS = 50;
export const retireSkusSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(SKU_RETIREMENT_MAX_IDS),
    reason: z.string().trim().min(1).max(500),
});
export const unretireSkusSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(SKU_RETIREMENT_MAX_IDS),
});
/**
 * BLU-1276: read-only "is this SKU in use?" report. A tRPC query travels in
 * the URL, and the frontend's `httpBatchLink({ maxURLLength: 2083 })` REJECTS
 * a single op over that cap client-side ("Input is too big for a single
 * dispatch"). One encoded UUID costs ~45 URL chars, so ~43 fit; capped at 40.
 * Callers chunk larger selections (same precedent as LINK_STATE_GID_CHUNK in
 * apps/frontend/src/features/product-sync/lib/shared-stock.ts).
 */
export const SKU_USAGE_QUERY_MAX_IDS = 40;
export const getSkuUsageSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(SKU_USAGE_QUERY_MAX_IDS),
});
export const resolveStockSuggestionSchema = z.object({
    orderId: uuidSchema,
    skuId: uuidSchema,
    locationId: uuidSchema,
    suggestionType: stockSuggestionIssueTypeSchema,
    notes: mediumString.optional(),
});
/**
 * List products grouped by parent product (product → variant hierarchy)
 */
export const listProductsGroupedSchema = z
    .object({
    productionType: productListFilterSchema.optional(),
    search: z.string().max(200).optional(),
    /**
     * "Who can use it" (BLU-1394): Only us / All clients / Only one client, a
     * SQL filter on the paged query (so the count and the page describe one
     * set). Own-catalog only; ignored cross-org.
     */
    ...baseGarmentAudienceFilterFields,
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().max(100).optional(),
    /**
     * 3PL client-catalog view: read ANOTHER org's catalog (owner → active-client
     * only, Admin/SkusManage, costs + supplier stripped server-side).
     */
    targetOrganizationId: uuidSchema.optional(),
    /**
     * Cross-org only: the caller's currently-selected location. When it is one
     * of the junction locations authorising the view, each POD variant also
     * carries its per-(sku, location) `print_technology_override` at that floor
     * so the catalog badges reflect what will actually print there. Ignored on
     * own-catalog reads.
     */
    locationId: uuidSchema.optional(),
    /**
     * "Show archived": also return archived (retired) SKUs and products whose
     * variants are all archived. Own-catalog only; ignored cross-org; applied
     * in SQL on the paged query, so the count and the page describe one set.
     */
    includeArchived: z.boolean().optional(),
})
    .superRefine(refineBaseGarmentAudienceFilter);
/**
 * Get a single product with all variants
 */
export const getProductDetailSchema = z.object({
    productId: uuidSchema,
});
/**
 * Create a POD product with variants
 */
export const createProductWithVariantsSchema = z.object({
    title: shortString.min(1, 'Product title is required'),
    productType: shortString.optional(),
    imageUrl: z.string().url().optional().or(z.literal('')),
    variants: z.array(z.object({
        sku: shortString.min(1, 'SKU is required'),
        size: shortString.optional(),
        color: shortString.optional(),
        imageUrl: z.string().url().optional().or(z.literal('')),
    })).min(1, 'At least one variant is required'),
});
/**
 * Add a variant to an existing product
 */
export const addVariantSchema = z.object({
    productId: uuidSchema,
    sku: shortString.min(1, 'SKU is required'),
    size: shortString.optional(),
    color: shortString.optional(),
    imageUrl: z.string().url().optional().or(z.literal('')),
});
/**
 * Remove a variant from a product (unlinks but doesn't delete the SKU)
 */
export const removeVariantSchema = z.object({
    productId: uuidSchema,
    skuId: uuidSchema,
});
/**
 * Group ungrouped SKUs under a product (new or existing)
 */
export const groupSkusUnderProductSchema = z.object({
    skuIds: z.array(uuidSchema).min(1, 'Select at least one SKU'),
    productId: uuidSchema.optional(),
    newProduct: z.object({
        title: shortString.min(1, 'Product title is required'),
        productType: shortString.optional(),
        imageUrl: z.string().url().optional().or(z.literal('')),
    }).optional(),
}).refine((d) => (d.productId != null) !== (d.newProduct != null), { message: 'Provide either productId or newProduct, not both' });
/**
 * Delete a product and all its variants globally (POD only, admin only)
 */
export const deleteProductGlobalSchema = z.object({
    productId: uuidSchema,
});
/**
 * Bulk delete products globally (POD only, admin only). `force` overrides the
 * has-stock and has-projections refusals AFTER a second, stronger confirm in
 * the UI — never sent on the first attempt. `force` alone is NOT enough to
 * delete stocked variants: the request must also echo every stocked variant's
 * sku code in `forceAcknowledgedSkus` (the server re-checks stock and refuses
 * any stocked sku that was not explicitly acknowledged). This is a ceremony
 * gate, not a boundary — a determined API caller can list codes and echo
 * them; what it prevents is deleting stock NO request ever named.
 */
export const deleteProductsGlobalSchema = z.object({
    productIds: z.array(uuidSchema).min(1).max(50),
    force: z.boolean().optional(),
    // No .trim(): acks are compared RAW against skus.sku server-side, and a
    // stored sku carrying whitespace must stay acknowledgeable. Cap sized for
    // 50 products × a full garment family of stocked variants each.
    forceAcknowledgedSkus: z.array(z.string().min(1).max(120)).max(5000).optional(),
});
/**
 * Bulk update variant (SKU) fields across multiple variants.
 *
 * All fields optional — only provided fields are applied.
 * `undefined` = skip field, `null` = clear field.
 * All SKUs are fully editable (Phase 41 / BLU-262 — there is NO locked-PAP
 * skip). Exception: `printTechnology` (BLU-673) is applied to POD variants
 * only — PAP variants are deliberately skipped (print technology is
 * meaningless for pick-and-pack; see `resolvePrintTechnology`).
 */
export const bulkUpdateVariantsSchema = z.object({
    skuIds: z.array(uuidSchema).min(1).max(200),
    title: shortString.nullable().optional(),
    category: shortString.optional(),
    color: shortString.nullable().optional(),
    skuCostOverride: z.number().nonnegative().nullable().optional(),
    imageUrl: z.string().url().optional().or(z.literal('')).nullable().optional(),
    hsCode: z.string().max(20).nullable().optional(),
    weightValue: z.number().nonnegative().nullable().optional(),
    weightUnit: z.enum(['GRAMS', 'KILOGRAMS', 'OUNCES', 'POUNDS']).nullable().optional(),
    /** BLU-673: per-SKU print technology (POD only; null clears back to the
     *  location/method-rule default). */
    printTechnology: printTechnologySchema.nullable().optional(),
});
/**
 * Update a POD product's metadata (title, type, image)
 */
export const updateProductDetailsSchema = z.object({
    productId: uuidSchema,
    title: shortString.min(1, 'Product title is required').optional(),
    productType: shortString.optional(),
    imageUrl: z.string().url().optional().or(z.literal('')).or(z.null()),
    specPdfUrl: z.string().url().optional().or(z.literal('')).or(z.null()),
});
export const bulkAutoGroupSkusSchema = z.object({
    groups: z.array(z.object({
        title: shortString.min(1, 'Product title is required'),
        productType: shortString.optional(),
        imageUrl: z.string().url().optional().or(z.literal('')),
        skuIds: z.array(uuidSchema).min(1),
    })).min(1),
    /**
     * Production type for created product groups. PAP only: a POD request (or none, which
     * defaults to POD) is refused by the service, because blanks group themselves by
     * product code. 'POD' stays in the enum so a tab opened before that gets the clear refusal.
     */
    productionType: z.enum(['POD', 'PAP']).optional(),
});
/**
 * Products > POD tab > Group blanks, apply: the codes the preview said will change, so the
 * apply covers exactly what was previewed (a code that appeared since is left
 * for the next preview). No org: it always comes from auth. Capped at the SQL's
 * own limit of 500 codes per call.
 */
export const groupBlanksByCodeSchema = z.object({
    codes: z.array(z.string().regex(/^[A-Z0-9]{1,10}$/)).min(1).max(500),
});
/** Products > Bulk edit CSV: own-catalog Default cost, size and color; `cost` is raw cell text. */
export const bulkEditSkusSchema = z.object({
    dryRun: z.boolean(),
    items: z
        .array(z.object({
        sku: z.string().trim().min(1, 'SKU is required').max(64),
        cost: z.string().max(64).optional(),
        /**
         * BLU-1359: raw Currency cell. Absent keeps the stored currency, blank
         * means the org currency. A plain string on purpose: an unknown code is
         * a per-row skip (unsupported_currency), not a 400 for the whole file.
         */
        currency: z.string().max(64).optional(),
        size: shortString.optional(),
        color: shortString.optional(),
    }))
        .min(1, 'At least one item required')
        .max(10000),
    /** Uploaded file name, shown in Logs > Adjustments for an applied edit. */
    filename: z.string().trim().max(255).optional(),
});
// ============================================================================
// BULK CSV IMPORT
// ============================================================================
/**
 * BLU-3xx Phase 47.01.2-B-06: per-row + parent `targetOrganizationId` for
 * bulk CSV import at shared 3PL locations. Per-row takes precedence over
 * parent. Both optional; omitted = caller's org (single-tenant byte-
 * equivalent). Service-layer widens the SKU lookup to caller +
 * `accessibleOrgIds` only when at least one row carries a non-caller
 * target — at non-shared locations or single-tenant deployments the
 * lookup remains caller-only.
 */
export const bulkCsvImportSchema = z.object({
    locationId: uuidSchema,
    filename: z.string().max(500).optional(),
    /** Operator reference for the adjustment document header. */
    reference: adjustmentReferenceSchema.optional(),
    blockOnErrors: z.boolean().default(true),
    rows: z.array(z.object({
        sku: z.string().min(1).max(200),
        quantity: z.number().int().min(1).max(999999),
        targetOrganizationId: uuidSchema.optional(),
    })).min(1).max(2000),
    targetOrganizationId: uuidSchema.optional(),
});
export const undoBulkImportSchema = z.object({
    locationId: uuidSchema,
    batchId: uuidSchema,
    skuId: uuidSchema.optional(),
});
// ============================================================================
// BULK STOCKTAKE (SET stock levels from CSV)
// ============================================================================
/**
 * Max rows a single bulk-stocktake submit accepts.
 *
 * Deliberately its OWN constant, not shared with `bulkCsvImportSchema`'s
 * identical-looking 2000: they are different endpoints with different risk
 * profiles (stocktake SETS absolute stock, csv-import ADDS), and collapsing
 * them onto one constant means a future bump to one silently widens the other.
 *
 * The count-sheet exporter imports this so the export-time warning and the
 * import-time block can never drift from the server's actual limit.
 */
export const BULK_STOCKTAKE_MAX_ROWS = 2000;
/**
 * Filenames are display/audit metadata only (stored on `bulk_import_batches`),
 * never a filesystem path — so the charset only needs to be non-hostile, not
 * ASCII. The original `/^[\w\-. ]+\.csv$/i` rejected two things the count-sheet
 * flow produces constantly:
 *   - CJK names (`盘点表.csv`) — JS `\w` is `[A-Za-z0-9_]`, and this surface is
 *     explicitly built for Chinese warehouse operators.
 *   - the browser/Excel duplicate suffix (`count-sheet (1).csv`).
 * Both failed Zod at final submit, AFTER the operator had walked the warehouse.
 * `\p{L}\p{N}` + the `u` flag accepts any script; control chars, path
 * separators and quotes stay excluded.
 */
export const bulkStocktakeSchema = z.object({
    locationId: uuidSchema,
    filename: z.string().max(255).regex(/^[\p{L}\p{N}_\-. ()[\]]+\.csv$/iu, 'Invalid filename').optional(),
    /** Operator reference for the adjustment document header. */
    reference: adjustmentReferenceSchema.optional(),
    rows: z.array(z.object({
        sku: z.string().min(1).max(200),
        newStock: z.number().int().min(0).max(999999),
        targetOrganizationId: uuidSchema.optional(),
    })).min(1).max(BULK_STOCKTAKE_MAX_ROWS),
    targetOrganizationId: uuidSchema.optional(),
    /**
     * BLU-711 A2: SKUs the wizard left blank (not counted this round). Only
     * meaningful at a shared location whose junction is in `review` mode — the
     * service stages them on the resulting stocktake_proposals row as
     * `counted_qty: null` lines so the reviewer sees a "not counted" summary
     * alongside the counted ones. Bounded the same as `rows` — a count sheet
     * can never carry more not-counted SKUs than its row ceiling.
     *
     * The DIRECT-write path (own-org rows, or an `auto_apply` client) IGNORES
     * this field entirely: nothing is ever written for an uncounted SKU, same
     * as today (see the `BULK_STOCKTAKE_MAX_ROWS` doc above — blank stays
     * un-written, never coerced to zero).
     */
    uncountedRows: z.array(z.object({
        sku: z.string().min(1).max(200),
        targetOrganizationId: uuidSchema.optional(),
    })).max(BULK_STOCKTAKE_MAX_ROWS).optional(),
});
export const undoBulkStocktakeSchema = z.object({
    locationId: uuidSchema,
    batchId: uuidSchema,
});
/**
 * BLU-3xx Phase 47.01.2-B-02 / B-05: list recent receipts at a location for
 * the Receive History page + the post-receive Undo toast (which only needs
 * the latest 1-N rows). Cross-org-aware via service-layer accessibleOrgIds.
 *
 *   - `ownerOrgId` (optional): narrow to a single owner org. Validated
 *     against `accessibleOrgIds` server-side; FORBIDDEN if not accessible.
 *   - `limit` (default 50, max 200).
 *   - `cursor`: opaque token from the previous page's `nextCursor`.
 *     Encodes the compound `(createdAt, id)` pair so rows with
 *     identical `created_at` (common when 3PLs batch-import many rows
 *     in one transaction) are not skipped. The DAO does
 *     `(created_at, id) < cursor` lexicographically. (BLU-3xx Phase
 *     47.01.2-B-fix HIGH-1.)
 *   - `fromDate` (B-05, optional): ISO timestamp lower bound. The DAO does
 *     `created_at >= fromDate` so the Receive History page can default to
 *     last 7 days. For 3PLs doing hundreds of receipts/day, an unbounded
 *     window would make the list unscrollable; the page exposes 7d / 30d /
 *     90d / custom.
 */
export const listRecentReceiptsSchema = z.object({
    locationId: uuidSchema,
    ownerOrgId: uuidSchema.optional(),
    limit: z.number().int().min(1).max(200).optional(),
    cursor: z.string().optional(),
    fromDate: z.string().optional(),
});
/**
 * BLU-3xx Phase 47.01.2-B-02: per-receipt reversal. Takes the original
 * inventory_transactions.id (surfaced by addStock / setStock /
 * bulkAddStock / bulkStocktake response) and creates a compensating
 * transaction. Idempotent: a UNIQUE partial index on
 * (reference_id) WHERE reference_type='receipt_reversal' serializes
 * concurrent callers; the second arrival returns the existing reversal id.
 */
export const reverseReceiptSchema = z.object({
    transactionId: uuidSchema,
});
export const getBulkImportBatchesSchema = z.object({
    locationId: uuidSchema.optional(),
    /** 3PL per-client filter — validated against accessible orgs server-side. */
    clientOrgId: uuidSchema.optional(),
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(20),
});
export const getBulkImportBatchDetailSchema = z.object({
    batchId: uuidSchema,
    locationId: uuidSchema,
});
/** Per-SKU, per-field change list of one applied bulk edit (no location: org-level). */
export const getSkuEditChangesSchema = z.object({
    batchId: uuidSchema,
});
